import moment from 'moment'
import _ from 'lodash'
import actions from './absencesActions'
import { AbsencesAssistantState, AbsencesState } from '@/types/application-state'
import * as getterTypes from '@/store/getter-types'
import * as mutationTypes from '@/store/mutation-types'
import { ShiftEditorPlanningPayload } from '@/model/planning/ShiftEditorPlanningPayload'
import { CompanyFilterValue, PlanningAssistantPeriodicityPayload } from '@/types/payload'
import { EmployeeAvailableForPlanning } from '@/model/planning/EmployeeAvailableForPlanning'
import { Employee } from '@/model/companyStructure/Employee'
import { AbsenceReason } from '@/model/absences/AbsenceReason'
import { Absence } from '@/model/absences/Absence'
import { SelectedEmployee } from '@/model/absences/SelectedEmployee'
import { PeriodicityPlannedAbsenceEmployee } from '@/types'
import { AbsenceSerie } from '@/types/service-response'

const defaultAbsencesAssistantState = (): AbsencesAssistantState => {
  const startDate = moment().startOf('day').add(8, 'hour')
  const endDate = moment(startDate).add(8, 'hour')

  return {
    initialEmployeeList: [],

    companyStructure: {
      profitCenterList: [],
      departmentList: [],
      planningGroupList: [],
      employeeList: []
    },

    filterValue: {
      profitCenter: undefined,
      department: undefined,
      planningGroup: undefined,
      employee: undefined
    },

    absenceReasonItem: undefined,
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),

    taskList: [],
    employeeForPlaningList: [],
    filteredEmployeesByDepartment: [],

    periodicity: {
      isUnique: true,
      weekDayList: [],
      repeatEveryNthWeek: 1,
      expireAfterRepetitions: 0,
      expireAfterDate: undefined
    },

    plannedEmployeeList: [],
    periodicityPlannedEmployeeList: [],
    plannedEmployeeExchange: undefined,
    plannedEmployeeDeletion: undefined,
    absenceApprove: false,
    absenceDisapprove: false,
    currentAbsence: undefined,
    isEditingSerie: false,
    periodicitySeriesId: undefined,
    periodicitySerieData: undefined
  }
}

const state: AbsencesState = {
  absencesAssistant: defaultAbsencesAssistantState()
}

const mutations = {

  [mutationTypes.ABSENCES_ASSISTANT_RESET_STATE_MUTATION] (state: AbsencesState): void {
    Object.assign(state.absencesAssistant, defaultAbsencesAssistantState())
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_DATA_FROM_PAYLOAD_MUTATION] (state: AbsencesState, payload: ShiftEditorPlanningPayload): void {
    state.absencesAssistant.initialEmployeeList = payload.employeeList
    state.absencesAssistant.companyStructure = payload.companyStructure
    state.absencesAssistant.filterValue = payload.filterValue

    if (payload.eventTimePeriod) {
      state.absencesAssistant.startDate = payload.eventTimePeriod.start
      state.absencesAssistant.endDate = payload.eventTimePeriod.end
    }
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_START_DATE_MUTATION] (state: AbsencesState, payload: Date): void {
    state.absencesAssistant.startDate = payload
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_END_DATE_MUTATION] (state: AbsencesState, payload: Date): void {
    state.absencesAssistant.endDate = payload
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_REASON_ITEM_MUTATION] (state: AbsencesState, payload: AbsenceReason | undefined): void {
    state.absencesAssistant.absenceReasonItem = payload
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING_MUTATION] (state: AbsencesState, payload: EmployeeAvailableForPlanning[]): void {
    state.absencesAssistant.employeeForPlaningList = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST_MUTATION] (state: AbsencesState, payload: SelectedEmployee[]): void {
    state.absencesAssistant.plannedEmployeeList = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST_MUTATION] (state: AbsencesState, payload: PeriodicityPlannedAbsenceEmployee[]): void {
    state.absencesAssistant.periodicityPlannedEmployeeList = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_APPROVE_MUTATION] (state: AbsencesState, payload: boolean): void {
    state.absencesAssistant.absenceApprove = payload
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_ABSENCE_DISAPPROVE_MUTATION] (state: AbsencesState, payload: boolean): void {
    state.absencesAssistant.absenceDisapprove = payload
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_CURRENT_ABSENCE_MUTATION] (state: AbsencesState, payload: Absence): void {
    state.absencesAssistant.currentAbsence = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT_MUTATION] (state: AbsencesState, payload: Employee[]): void {
    state.absencesAssistant.filteredEmployeesByDepartment = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_MUTATION] (state: AbsencesState, payload: PlanningAssistantPeriodicityPayload): void {
    state.absencesAssistant.periodicity = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_IS_EDITING_SERIE_MUTATION] (state: AbsencesState, payload: boolean): void {
    state.absencesAssistant.isEditingSerie = _.cloneDeep(payload)
  },

  [mutationTypes.ABSENCES_ASSISTANT_SET_PERIODICITY_SERIE_MUTATION] (state: AbsencesState, payload: AbsenceSerie | undefined): void {
    state.absencesAssistant.periodicitySerieData = _.cloneDeep(payload)
  }
}

const getters = {

  [getterTypes.ABSENCES_ASSISTANT_GET_INITIAL_EMPLOYEE_LIST] (state: AbsencesState): Employee[] {
    return state.absencesAssistant.initialEmployeeList
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_START_DATE] (state: AbsencesState): Date {
    return state.absencesAssistant.startDate
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_END_DATE] (state: AbsencesState): Date {
    return state.absencesAssistant.endDate
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_COMPANY_FILTER] (state: AbsencesState): CompanyFilterValue {
    return state.absencesAssistant.filterValue
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_PERIODICITY] (state: AbsencesState): PlanningAssistantPeriodicityPayload {
    return state.absencesAssistant.periodicity
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_PLANNED_EMPLOYEE_LIST] (state: AbsencesState): SelectedEmployee[] {
    return state.absencesAssistant.plannedEmployeeList
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_PERIODICITY_PLANNED_EMPLOYEE_LIST] (state: AbsencesState): PeriodicityPlannedAbsenceEmployee[] {
    return state.absencesAssistant.periodicityPlannedEmployeeList
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_EMPLOYEE_LIST_FOR_PLANNING] (state: AbsencesState): EmployeeAvailableForPlanning[] {
    return state.absencesAssistant.employeeForPlaningList
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_ABSENCE_REASON_ITEM] (state: AbsencesState): AbsenceReason | undefined {
    return state.absencesAssistant.absenceReasonItem
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_ABSENCE_APPROVE] (state: AbsencesState): boolean {
    return state.absencesAssistant.absenceApprove
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_ABSENCE_DISAPPROVE] (state: AbsencesState): boolean {
    return state.absencesAssistant.absenceDisapprove
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_CURRENT_ABSENCE] (state: AbsencesState): Absence | undefined {
    return state.absencesAssistant.currentAbsence
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_IS_EDITING_SERIE] (state: AbsencesState): boolean {
    return state.absencesAssistant.isEditingSerie
  },

  [getterTypes.ABSENCES_ASSISTANT_GET_PERIODICITY_SERIE] (state: AbsencesState): AbsenceSerie | undefined {
    return state.absencesAssistant.periodicitySerieData
  }
}

const absencesModule = {
  state,
  getters,
  mutations,
  actions
}

export default absencesModule
